import { Field, type FieldProps, Formik } from 'formik';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { object, string } from 'yup';

import {
  type LeadFormTrigger,
  type LeadFormUILocation,
  trackGTMEvent,
  useTrackClickedLeadFormSubmit,
  useTrackLeadFormOpened,
} from '@air/analytics';
import { FormikField } from '@air/formik-field';
import { Button } from '@air/primitive-button';
import { Input } from '@air/primitive-input';
import { Modal, ModalCloseButton, ModalDescription, ModalTitle } from '@air/primitive-modal';

import Form from '~/components/Form';
import { FormikSingleSelect } from '~/components/Formik/FormikSingleSelect';
import { Routes } from '~/constants/routes';
import { useAccountContext } from '~/providers/AccountProvider';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';

const COMPANY_SIZE_OPTIONS = [
  { label: '1-10', value: '1-10' },
  { label: '11-50', value: '11-50' },
  { label: '51-100', value: '51-100' },
  { label: '101-500', value: '101-500' },
  { label: '501-1000', value: '501-1000' },
  { label: '1001+', value: '1001+' },
];

const validationSchema = object({
  firstName: string().default('').required('First name is required'),
  lastName: string().default('').required('Last name is required'),
  email: string()
    .email()
    .test(
      'Please enter a business email',
      'Please enter a business email',
      (value) =>
        !value
          ?.toLowerCase()
          ?.match(
            /[a-zA-Z0-9]{0,}([.]?[a-zA-Z0-9]{1,})[@](aol.com|comcast.net|gmail.com|gmail.co|gmail.to|gmx.com|hotmail.com|icloud.com|live.com|mac.com|mail.ck|mail.com|mailbird.com|me.com|msn.com|mymail.com|outlook.com|protonmail.com|postbox.com|rocketmail.com|sbcglobal.net|taskmonk.ai|test.com|thunderbird.com|turtlewig.com|zoho.com|yahoo.com|yandex.mail)/g,
          ),
    )
    .test(
      'Please enter a personal business email',
      'Please enter a personal business email',
      (value) => !value?.toLowerCase()?.match(/(info|hello)[@].*/g),
    )
    .required('Email address is required')
    .default(''),
  company_size: string().default('').required('Company Size is required'),
  phone: string().default('').required('Phone number is required'),
  message: string().default(''),
});

type FormValues = {
  firstName: string;
  lastName: string;
  email: string;
  company_size: string;
  phone: string;
  message: string;
};

export type DemoFormModalProps = {
  trigger?: LeadFormTrigger;
  uiLocation?: LeadFormUILocation;
};

const DemoForm = ({ onClose, uiLocation }: { onClose: () => void; uiLocation?: LeadFormUILocation }) => {
  const { data: account } = useAccountContext();
  const { trackClickedLeadFormSubmit } = useTrackClickedLeadFormSubmit();

  const initialValues: FormValues = {
    ...(validationSchema.cast(undefined) as FormValues),
    firstName: account?.firstName ?? '',
    lastName: account?.lastName ?? '',
    email: account?.email ?? '',
    phone: account?.phone ?? '',
  };

  const handleSubmit = useCallback(
    (values: FormValues, { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }) => {
      trackClickedLeadFormSubmit({ email: values.email, ui_location: uiLocation });

      trackGTMEvent({
        event: `Lead Form Submit`,
        eventProps: {
          label: 'lead form submit',
          category: 'enterprise',
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          company_size: values.company_size,
          phone: values.phone,
          message: values.message,
        },
      });

      // Default.com will handle the form submission automatically
      // The scheduler will appear as its own overlay, so we can close the form modal

      setSubmitting(false);
      onClose(); // Close the form modal immediately after successful submission
    },
    [trackClickedLeadFormSubmit, uiLocation, onClose],
  );

  return (
    <>
      <Formik<FormValues>
        enableReinitialize
        initialValues={initialValues}
        validateOnMount
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ isSubmitting, isValid }) => (
          <Form className="flex flex-col gap-6" data-default-form-id="878112">
            <input type="hidden" name="lead_source" value="Web (demo request)" />
            <input type="hidden" name="lead_source_detail" value="Web form - In-app" />
            <div className="flex flex-col gap-4 md:flex-row">
              <FormikField
                className="w-full"
                component={
                  <Input
                    autoComplete="given-name"
                    data-1p-ignore
                    placeholder="First name*"
                    required
                    size="large"
                    type="text"
                  />
                }
                id="firstName"
                isLabelHidden
                label="First name"
                name="firstName"
              />
              <FormikField
                className="w-full"
                component={
                  <Input
                    autoComplete="family-name"
                    data-1p-ignore
                    placeholder="Last name*"
                    required
                    size="large"
                    type="text"
                  />
                }
                id="lastName"
                isLabelHidden
                label="Last name"
                name="lastName"
              />
            </div>

            <FormikField
              className="w-full"
              component={
                <Input
                  autoComplete="email"
                  data-1p-ignore
                  placeholder="Email address*"
                  required
                  size="large"
                  type="email"
                />
              }
              id="email"
              isLabelHidden
              label="Email address"
              name="email"
            />
            <Field name="company_size">
              {({ field }: FieldProps<string>) => (
                <>
                  <input type="hidden" name="Company size" value={field.value} />
                  <FormikSingleSelect
                    isLabelHidden
                    label="Company size"
                    name="company_size"
                    options={COMPANY_SIZE_OPTIONS}
                    placeholder="Company size*"
                    size="extra-large"
                  />
                </>
              )}
            </Field>
            <FormikField
              className="w-full"
              component={
                <Input autoComplete="tel" data-1p-ignore placeholder="Phone number*" size="large" type="tel" />
              }
              id="phone"
              isLabelHidden
              label="Phone number"
              name="phone"
            />
            <FormikField
              className="w-full"
              component={<Input data-1p-ignore placeholder="Message" size="large" type="text" />}
              id="message"
              isLabelHidden
              label="Message"
              name="message"
            />
            <Button appearance="filled" color="blue" size="large" type="submit" disabled={isSubmitting || !isValid}>
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </Button>
          </Form>
        )}
      </Formik>
      <p className="text-center text-14 text-grey-11">
        By submitting the form, you agree to our <a href={Routes.marketing.terms}>Terms of Service</a> and acknowledge
        our <a href={Routes.marketing.privacy}>Privacy Policy</a>.
      </p>
    </>
  );
};

export const DemoFormModal = memo(({ onClose, trigger, uiLocation }: AirModalProps<DemoFormModalProps>) => {
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [configSet, setConfigSet] = useState(false);
  const { data: subscription } = useSubscriptionContext();
  const { trackLeadFormOpened } = useTrackLeadFormOpened();
  const router = useRouter();

  const triggerValue = trigger ?? `page:${router.pathname}`;
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    if (!hasTrackedRef.current && subscription?.plan) {
      trackLeadFormOpened({ plan: subscription.plan, trigger: triggerValue });
      hasTrackedRef.current = true;
    }
  }, [subscription?.plan, trackLeadFormOpened, triggerValue]);

  useEffect(() => {
    // Set configuration BEFORE script loads
    if (typeof window !== 'undefined') {
      window.__default__ = {
        form_id: 878112,
        team_id: 401,
        listenToIds: [],
      };
      setConfigSet(true);

      // Check if script already exists
      const existingScript = document.getElementById('default-sdk-script');

      if (existingScript) {
        setIsScriptLoaded(true);
      }
    }
  }, []);

  return (
    <>
      {configSet && (
        <Script
          id="default-sdk-script"
          src="https://import-cdn.default.com"
          strategy="afterInteractive"
          onLoad={() => {
            setIsScriptLoaded(true);
          }}
          onError={() => {
            setIsScriptLoaded(true); // Still show form as fallback
          }}
        />
      )}
      <Modal
        className="flex flex-col gap-4"
        dangerouslyBypassFocusLock
        dangerouslyBypassScrollLock
        isOpen
        onDismiss={onClose}
        data-testid="DEMO_FORM_MODAL"
      >
        <header className="flex flex-col gap-2">
          <div className="flex justify-between gap-2">
            <ModalTitle>Let&apos;s talk</ModalTitle>
            <ModalCloseButton onClick={onClose} />
          </div>
          <ModalDescription>
            Schedule a free demo today. One of our experts will show you how Air works.
          </ModalDescription>
        </header>

        {!isScriptLoaded ? (
          <div className="flex justify-center py-8">
            <span className="text-grey-11">Loading...</span>
          </div>
        ) : (
          <DemoForm onClose={onClose} uiLocation={uiLocation} />
        )}
      </Modal>
    </>
  );
});

DemoFormModal.displayName = 'DemoFormModal';
